import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { Save } from '@mui/icons-material';
import {
    Alert,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { PlanningLevelItemsType } from 'src/domain/models/split-modules';
import { useCreateNewBuyingEvent } from 'src/hooks/buying-event-managment/create/useCreateNewBuyingEvent';
import { useUpdateBuyingEvent } from 'src/hooks/buying-event-managment/update/useUpdateBuyingEvent';
import { useSaveShortcut } from 'src/hooks/shortcuts/useSaveShortcut';
import { formatDateToMonthKey } from 'src/utils/monthKeys';
import { DialogTitleWithCloseIcon } from '../DialogTitleWithCloseIcon/DialogTitleWithCloseIcon';

interface Props {
    id?: number | null;
    eventName?: string | null;
    eventOrderMonth?: Date | null;
    initialDeliveryRangeStart?: Date | null;
    initialDeliveryRangeEnd?: Date | null;
    plannedOnIds?: number[] | null;
    planningLevelList: PlanningLevelItemsType | undefined;
    open: boolean;
    handleCloseDialog: () => void;
    handleRefetch: () => void;
}

export const BuyingEventDialog: FC<Props> = ({
    eventName,
    eventOrderMonth,
    initialDeliveryRangeStart,
    initialDeliveryRangeEnd,
    plannedOnIds,
    open,
    planningLevelList,
    handleCloseDialog,
    handleRefetch,
    id,
}) => {
    const today = useMemo(() => new Date(), []);
    const tomorrow = useMemo(() => new Date(today.getTime() + 24 * 60 * 60 * 1000), [today]);

    const [error, setError] = useState(false);
    const [orderMonth, setOrderMonth] = useState(eventOrderMonth ?? today);
    const [deliveryRangeStart, setDeliveryRangeStart] = useState(initialDeliveryRangeStart ?? today);
    const [deliveryRangeEnd, setDeliveryRangeEnd] = useState(initialDeliveryRangeEnd ?? tomorrow);
    const [name, setName] = useState(eventName ?? '');
    const [plannedOnIdsList, setPlannedOnIdsList] = useState<number[]>(plannedOnIds ?? []);
    const [dialogOpen, setDialogOpen] = useState(open ?? false);
    const isEditing = id !== undefined;

    const [createEvent, { loading: creatingEvent }] = useCreateNewBuyingEvent({
        initialDeliveryRangeStart: formatDateToMonthKey(deliveryRangeStart),
        initialDeliveryRangeEnd: formatDateToMonthKey(deliveryRangeEnd),
        name,
        orderMonth: formatDateToMonthKey(orderMonth),
        plannedOnIds: plannedOnIdsList,
    });

    const [updateEvent, { loading: updatingEvent }] = useUpdateBuyingEvent({
        id,
        name,
        initialDeliveryRangeStart: formatDateToMonthKey(deliveryRangeStart),
        initialDeliveryRangeEnd: formatDateToMonthKey(deliveryRangeEnd),
        orderMonth: formatDateToMonthKey(orderMonth),
        plannedOnIds: plannedOnIdsList,
    });

    const loading = updatingEvent || creatingEvent;

    // make sure deliveryRangeEnd is always later than deliveryRangeStart
    // biome-ignore lint/correctness/useExhaustiveDependencies: auto update delivery end when start changes, do nothing if end changes
    useEffect(() => {
        if (deliveryRangeStart > deliveryRangeEnd)
            setDeliveryRangeEnd(new Date(deliveryRangeStart.getTime() + 24 * 60 * 60 * 1000));
    }, [deliveryRangeStart]);

    const setInitialValues = useCallback(() => {
        if (eventName) setName(eventName);
        if (eventOrderMonth) setOrderMonth(eventOrderMonth);
        if (initialDeliveryRangeStart) setDeliveryRangeStart(initialDeliveryRangeStart);
        if (initialDeliveryRangeEnd) setDeliveryRangeEnd(initialDeliveryRangeEnd);
        if (plannedOnIds) setPlannedOnIdsList(plannedOnIds);
        if (open) setDialogOpen(open);
    }, [initialDeliveryRangeEnd, initialDeliveryRangeStart, eventName, eventOrderMonth, plannedOnIds, open]);

    useEffect(() => {
        setInitialValues();
    }, [setInitialValues]);

    const handleSelectedItem = useCallback(
        (id: number) => {
            const index = plannedOnIdsList.findIndex((item) => item === id);
            const newArray = [...plannedOnIdsList];
            if (index !== -1) {
                newArray.splice(index, 1);
            } else {
                newArray.push(id);
            }
            setPlannedOnIdsList(newArray);
        },
        [plannedOnIdsList]
    );

    const closeDialogue = useCallback(() => {
        setDialogOpen(false);
        handleCloseDialog();
    }, [handleCloseDialog]);

    const resetEvent = useCallback(() => {
        if (isEditing) {
            setInitialValues();
        } else {
            setOrderMonth(today);
            setDeliveryRangeStart(today);
            setDeliveryRangeEnd(tomorrow);
            setPlannedOnIdsList([]);
            setName('');
            setError(false);
        }
    }, [today, tomorrow, setInitialValues, isEditing]);

    const onCancel = useCallback(() => {
        closeDialogue();
        resetEvent();
    }, [closeDialogue, resetEvent]);

    const handleSaveEvent = useCallback(async () => {
        const response = id !== undefined ? await updateEvent() : await createEvent();

        if (response) {
            handleRefetch();
            closeDialogue();
            resetEvent();
        }
    }, [closeDialogue, createEvent, handleRefetch, id, resetEvent, updateEvent]);

    const canSave = useCallback(() => {
        if (!name || !orderMonth || !deliveryRangeStart || !deliveryRangeEnd || !plannedOnIdsList.length) return false;
        return true;
    }, [deliveryRangeEnd, deliveryRangeStart, name, orderMonth, plannedOnIdsList.length]);

    const showOverwriteWarning = useMemo(() => {
        if (id !== undefined && initialDeliveryRangeStart && initialDeliveryRangeEnd) {
            return initialDeliveryRangeStart < deliveryRangeStart || initialDeliveryRangeEnd > deliveryRangeEnd;
        }
        return false;
    }, [initialDeliveryRangeEnd, initialDeliveryRangeStart, deliveryRangeEnd, deliveryRangeStart, id]);

    useSaveShortcut(handleSaveEvent, !canSave());
    useKeyboardShortcut({
        shortcutName: 'CLEAR',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            resetEvent();
        },
        capture: true,
    });

    return (
        <Dialog open={dialogOpen} onClose={closeDialogue} data-testid="buyingEventDialog">
            <DialogTitleWithCloseIcon
                title={isEditing ? 'Edit buying event' : 'Create buying event'}
                handleClose={closeDialogue}
            />
            <DialogContent sx={{ width: '400px', maxWidth: '100%', boxSizing: 'border-box' }}>
                <Collapse
                    in={showOverwriteWarning}
                    timeout={1000}
                    sx={{ marginBottom: showOverwriteWarning ? '10px' : '0px' }}
                >
                    <Alert severity="warning">
                        These changes made to the delivery range may overwrite Open To Buy Limits outside of the new
                        range.
                    </Alert>
                </Collapse>
                <Stack
                    sx={{
                        gap: 3,
                    }}
                >
                    <TextField
                        label="Event name"
                        id="buying-event-name-input"
                        data-testid="buyingEventName"
                        placeholder="New event name"
                        error={error}
                        onBlur={() => {
                            if (!name) setError(true);
                            else setError(false);
                        }}
                        onChange={(e) => {
                            setName(e.target.value);
                            if (e.target.value) setError(false);
                        }}
                        value={name}
                        required
                        helperText={error ? 'Event name is rquired' : undefined}
                    />
                    <DatePicker
                        portalId="order-month"
                        selected={orderMonth}
                        onChange={(date: Date | null) => date && setOrderMonth(date)}
                        dateFormat="MM/yyyy"
                        showMonthYearPicker
                        customInput={<TextField required fullWidth label="Order month *" />}
                    />

                    <Stack
                        direction="column"
                        sx={{
                            gap: 3,
                            mt: 2,
                        }}
                    >
                        <Typography variant="h6">Delivery range</Typography>
                        <DatePicker
                            portalId="delivery-range-start"
                            selected={deliveryRangeStart}
                            onChange={(date: Date | null) => date && setDeliveryRangeStart(date)}
                            selectsStart
                            startDate={deliveryRangeStart}
                            endDate={deliveryRangeEnd}
                            dateFormat="MM/yyyy"
                            showMonthYearPicker
                            customInput={
                                <TextField
                                    required
                                    fullWidth
                                    label="Start month *"
                                    slotProps={{
                                        htmlInput: { 'data-testid': 'deliveryRangeStart' },
                                    }}
                                />
                            }
                        />
                        <DatePicker
                            portalId="delivery-range-end"
                            selected={deliveryRangeEnd}
                            onChange={(date: Date | null) => date && setDeliveryRangeEnd(date)}
                            selectsEnd
                            startDate={deliveryRangeStart}
                            endDate={deliveryRangeEnd}
                            minDate={deliveryRangeStart}
                            dateFormat="MM/yyyy"
                            showMonthYearPicker
                            customInput={
                                <TextField
                                    required
                                    fullWidth
                                    label="End month *"
                                    slotProps={{
                                        htmlInput: { 'data-testid': 'deliveryRangeEnd' },
                                    }}
                                />
                            }
                        />
                    </Stack>
                    <Stack
                        direction="row"
                        sx={{
                            flexWrap: 'wrap',
                            gap: 1,
                            alignItems: 'center',
                            mt: 2,
                        }}
                    >
                        {planningLevelList?.map((item) => (
                            <Chip
                                key={item.id}
                                label={item.name}
                                color="info"
                                onClick={() => handleSelectedItem(item.id)}
                                variant={plannedOnIdsList.includes(item.id) ? 'filled' : 'outlined'}
                            />
                        ))}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="text" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    loading={loading}
                    loadingPosition="start"
                    startIcon={<Save />}
                    variant="contained"
                    size="medium"
                    color="info"
                    type="submit"
                    disabled={!canSave()}
                    onClick={handleSaveEvent}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};
